import {GroupApplicantDemographics} from 'ApplicationEntry/models/GroupApplicantDemographics';
import {NewYorkApplicantDemographics} from 'ApplicationEntry/models/NewYorkApplicantDemographics';
import {OrdinaryApplicantDemographics} from 'ApplicationEntry/models/OrdinaryApplicantDemographics';

export type ApplicantDemographics =
  | GroupApplicantDemographics
  | NewYorkApplicantDemographics
  | OrdinaryApplicantDemographics;

type ExhaustiveApplicantDemographicsOptionsByType<TReturn> = {
  Group: (group: GroupApplicantDemographics) => TReturn;
  NewYork: (newYork: NewYorkApplicantDemographics) => TReturn;
  Ordinary: (ordinary: OrdinaryApplicantDemographics) => TReturn;
};

type PartialApplicantDemographicsOptionsByType<TReturn> = {
  Group?: (group: GroupApplicantDemographics) => TReturn;
  NewYork?: (newYork: NewYorkApplicantDemographics) => TReturn;
  Ordinary?: (ordinary: OrdinaryApplicantDemographics) => TReturn;
  _: (app: ApplicantDemographics) => TReturn;
};

const isExhaustiveByType = <TReturn>(
  options: ApplicantDemographicsByTypeOptions<TReturn>
): options is ExhaustiveApplicantDemographicsOptionsByType<TReturn> =>
  !!options.Group && !!options.NewYork && !!options.Ordinary;

type ApplicantDemographicsByTypeOptions<TReturn> =
  | PartialApplicantDemographicsOptionsByType<TReturn>
  | ExhaustiveApplicantDemographicsOptionsByType<TReturn>;

export const matchApplicantDemographicsByType = <TReturn>(options: ApplicantDemographicsByTypeOptions<TReturn>) => (
  app: ApplicantDemographics
): TReturn => {
  switch (app.key) {
    case 'InvalidGroupApplicantDemographics':
    case 'ValidGroupApplicantDemographics':
      return isExhaustiveByType(options) ? options.Group(app) : options.Group ? options.Group(app) : options._(app);
    case 'InvalidNewYorkApplicantDemographics':
    case 'ValidNewYorkApplicantDemographics':
      return isExhaustiveByType(options)
        ? options.NewYork(app)
        : options.NewYork
        ? options.NewYork(app)
        : options._(app);
    case 'InvalidOrdinaryApplicantDemographics':
    case 'ValidOrdinaryApplicantDemographics':
      return isExhaustiveByType(options)
        ? options.Ordinary(app)
        : options.Ordinary
        ? options.Ordinary(app)
        : options._(app);
  }
};

export const isValidApplicantDemographics = (appDemographics: ApplicantDemographics) =>
  appDemographics.key === 'ValidGroupApplicantDemographics' ||
  appDemographics.key === 'ValidNewYorkApplicantDemographics' ||
  appDemographics.key === 'ValidOrdinaryApplicantDemographics';
